import { useState } from 'react';
import { useBoardPermission } from '@/hooks/use-boards';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import type { OverviewSprint } from '@hygge/shared';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  useCreateSprint,
  useDeleteSprint,
  useReorderSprints,
} from '@/hooks/use-sprints';
import { extractErrorMessage } from '@/lib/errors';
import { StartSprintDialog } from '../start-sprint-dialog';
import { CompleteSprintDialog } from '../complete-sprint-dialog';
import { SprintBlock } from './sprint-block';

interface SprintBlocksSectionProps {
  boardId: string;
  sprints: OverviewSprint[];
  activeSprintId: string | null;
  onOpenTask: (key: string) => void;
}

export function SprintBlocksSection({
  boardId,
  sprints,
  activeSprintId,
  onOpenTask,
}: SprintBlocksSectionProps) {
  const canManage = useBoardPermission(boardId, 'sprint.manage');
  // Deleting a sprint is sprint management, not task deletion — the endpoint
  // asks this board for `sprint.manage` and nothing else. Gating the control on
  // the delete permission hid it from people the backend would have allowed.
  const canDelete = useBoardPermission(boardId, 'sprint.manage');
  const createSprint = useCreateSprint(boardId);
  const deleteSprint = useDeleteSprint(boardId);
  const reorderSprints = useReorderSprints(boardId);

  const [startSprintId, setStartSprintId] = useState<string | null>(null);
  const [completeSprintId, setCompleteSprintId] = useState<string | null>(null);
  const [deleteSprintId, setDeleteSprintId] = useState<string | null>(null);
  // Optimistic order held only during/after a drag so the list doesn't snap back
  // before the server round-trip settles (mirrors the Kanban board-view pattern).
  const [order, setOrder] = useState<string[] | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const sprintIds = order ?? sprints.map((s) => s.id);
  const orderedSprints = sprintIds
    .map((id) => sprints.find((s) => s.id === id))
    .filter((s): s is OverviewSprint => s != null);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const current = sprints.map((s) => s.id);
    const oldIdx = current.indexOf(active.id as string);
    const newIdx = current.indexOf(over.id as string);
    if (oldIdx === -1 || newIdx === -1) return;

    const next = arrayMove(current, oldIdx, newIdx);
    setOrder(next);
    reorderSprints.mutate(next, {
      onError: (e) => toast.error(extractErrorMessage(e, 'Failed to reorder sprints')),
      onSettled: () => setOrder(null),
    });
  };

  const deleteTarget = sprints.find((s) => s.id === deleteSprintId) ?? null;
  const deleteIssueCount = deleteTarget?.issues.length ?? 0;

  const handleCreateSprint = () => {
    createSprint.mutate(
      { name: `Sprint ${sprints.length + 1}` },
      { onError: (e) => toast.error(extractErrorMessage(e, 'Failed to create sprint')) },
    );
  };

  const confirmDelete = () => {
    if (!deleteSprintId) return;
    deleteSprint.mutate(deleteSprintId, {
      onError: (e) => toast.error(extractErrorMessage(e, 'Failed to delete sprint')),
    });
    setDeleteSprintId(null);
  };

  return (
    <section>
      <div className="mb-3 flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Sprints
          <span className="tabular-nums text-muted-foreground/70">{sprints.length}</span>
        </h2>
        {canManage && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleCreateSprint}
            disabled={createSprint.isPending}
            className="h-7 gap-1.5 text-xs"
          >
            <Plus className="h-3.5 w-3.5" />
            Sprint
          </Button>
        )}
      </div>

      {sprints.length === 0 ? (
        <p className="rounded-lg border border-dashed border-border px-3 py-6 text-center text-sm text-muted-foreground">
          No sprints yet.
        </p>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={sprintIds} strategy={verticalListSortingStrategy}>
            <div className="space-y-2">
              {orderedSprints.map((sprint) => (
                <SprintBlock
                  key={sprint.id}
                  sprint={sprint}
                  boardId={boardId}
                  canManage={canManage}
                  canDelete={canDelete}
                  isAnotherSprintActive={!!activeSprintId && activeSprintId !== sprint.id}
                  onStart={setStartSprintId}
                  onComplete={setCompleteSprintId}
                  onDelete={setDeleteSprintId}
                  onOpenTask={onOpenTask}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* Lifecycle dialogs (reused). Mounted only while open so the completion
          dialog's task query doesn't fetch on every board overview render. */}
      {startSprintId && (
        <StartSprintDialog
          sprintId={startSprintId}
          projectId={boardId}
          open
          onOpenChange={(o) => !o && setStartSprintId(null)}
        />
      )}
      {completeSprintId && (
        <CompleteSprintDialog
          sprintId={completeSprintId}
          projectId={boardId}
          open
          onOpenChange={(o) => !o && setCompleteSprintId(null)}
        />
      )}

      {/* Delete confirmation */}
      <AlertDialog
        open={!!deleteSprintId}
        onOpenChange={(o) => !o && setDeleteSprintId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete sprint?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteIssueCount > 0
                ? `${deleteIssueCount} issue${deleteIssueCount === 1 ? '' : 's'} will be moved back to the backlog.`
                : 'This sprint has no issues.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive/10 text-destructive hover:bg-destructive/20 border border-destructive/20"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
